import React, { useEffect, useMemo } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import { MySelect } from "../../../components/ui/Select";
import { cn } from "../../../lib/utils";
import { Partner } from "../@types";
import { useGetPartnersMutation } from "../partnerApiSlice";

interface PartnerDropdownMenuProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showLabel?: boolean;
  error?: string;
}

interface PartnerOption {
  value: string | null;
  label: string;
}

const PartnerDropdownMenu: React.FC<PartnerDropdownMenuProps> = ({
  value,
  onChange,
  className,
  disabled,
  placeholder = "Partner",
  name = "partner",
  id = "partner",
  showLabel = true,
  error,
}) => {
  const [
    getPartners,
    { data: partnerData, isLoading, isError, error: errorData },
  ] = useGetPartnersMutation();

  const partners = useMemo(() => {
    return partnerData?.data.data || [];
  }, [partnerData?.data.data]);

  useEffect(() => {
    getPartners({
      pageNumber: 0,
      pageSize: 100,
      assigned: true,
    });
  }, [getPartners]);

  const partnerOptions: PartnerOption[] = React.useMemo(() => {
    if (isLoading) {
      return [{ value: null, label: "Loading..." }];
    }

    if (isError) {
      return [{ value: null, label: "Error loading partners" }];
    }

    const data: PartnerOption[] = [
      { value: "__none__", label: "-- None --" },
      ...partners.map((item: Partner) => ({
        value: item.id,
        label: item.fullName,
      })),
    ];

    return data;
  }, [partners, isLoading, isError]);

  const handleValueChange = (option: PartnerOption) => {
    if (option.value === "__none__") {
      onChange?.(null);
    } else {
      onChange?.(option.value);
    }
  };

  // Helper functions for MySelect
  const getValue = (option: PartnerOption) => option.value ?? "__none__";
  const getName = (option: PartnerOption) => option.label;

  return (
    <div className={cn("w-full")}>
      <MySelect<PartnerOption>
        name={name}
        id={id}
        label={showLabel ? "Partner" : ""}
        options={partnerOptions}
        getValue={getValue}
        getName={getName}
        value={value ?? "__none__"}
        onChange={onChange}
        onValChange={handleValueChange}
        placeholder={placeholder}
        disabled={disabled || isLoading || isError}
        className={cn(
          "focus:ring-primary focus:border-primary",
          {
            "opacity-50": isLoading,
          },
          className
        )}
        showSearch={true}
      />

      {isError && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {getErrorDetails(errorData)?.message || "Error loading partners"}
        </p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default PartnerDropdownMenu;
